import { useCallback, useRef } from 'react'

/**
 * Hook for managing node data synchronization with collaborative state.
 * Provides a properly configured syncNodeData function that follows
 * the established pattern and prevents data corruption.
 * 
 * @param nodeId - The ID of the node
 * @param updateNodeData - Function to update node data in the store
 * @param enableDebug - Whether to enable debug logging
 */
export function useNodeData(
    nodeId: string,
    updateNodeData: (id: string, updater: (prevData: any) => any) => void,
    enableDebug = false
) {
    // Track last synced data to maintain consistency
    const lastSyncedData = useRef<Record<string, any>>({})

    /**
     * Sync data to collaborative state following the syncNodeData pattern.
     * This function prevents recursive nesting and maintains data consistency.
     */
    const syncNodeData = useCallback((newData: Record<string, any>) => {
        updateNodeData(nodeId, (prevData) => {
            // Clean previous data to prevent recursive nesting
            const cleanPrevData = { ...prevData }
            if ('data' in cleanPrevData && typeof cleanPrevData.data === 'object' && cleanPrevData.data !== null) {
                delete cleanPrevData.data // Prevent recursive nesting
            }

            // Merge with new data
            const updatedData = {
                ...cleanPrevData,
                ...newData,
                lastModified: Date.now() // Always update lastModified
            }

            // Update tracking reference
            lastSyncedData.current = { ...lastSyncedData.current, ...newData }

            if (enableDebug) {
                console.log(`[NodeData ${nodeId}] syncNodeData:`, {
                    prevData: cleanPrevData,
                    newData,
                    updatedData
                })
            }

            return updatedData
        })
    }, [nodeId, updateNodeData, enableDebug])

    /**
     * Check if external data has changed compared to last synced data.
     * Used to detect when collaborative state has been updated by others.
     */
    const hasExternalChange = useCallback((currentData: Record<string, any>, fields: string[]) => {
        return fields.some(field => {
            const currentValue = currentData[field]
            const lastSyncedValue = lastSyncedData.current[field]

            // Handle object comparison
            if (typeof currentValue === 'object' && typeof lastSyncedValue === 'object') {
                return JSON.stringify(currentValue) !== JSON.stringify(lastSyncedValue)
            }

            return currentValue !== lastSyncedValue
        })
    }, [])

    /**
     * Update the last synced data reference.
     * Call this when initializing state from collaborative data.
     */
    const updateLastSyncedData = useCallback((data: Record<string, any>) => {
        lastSyncedData.current = { ...lastSyncedData.current, ...data }
    }, [])

    /**
     * Batch multiple field updates into a single sync operation.
     * More efficient than multiple individual syncNodeData calls.
     */
    const batchSyncNodeData = useCallback((updates: Record<string, any>[]) => {
        const batchedData = updates.reduce((acc, update) => ({ ...acc, ...update }), {})
        syncNodeData(batchedData)
    }, [syncNodeData])

    return {
        /** Sync data to collaborative state */
        syncNodeData,
        /** Check if data has changed externally */
        hasExternalChange,
        /** Update last synced data reference */
        updateLastSyncedData,
        /** Batch multiple updates */
        batchSyncNodeData,
        /** Get last synced data for debugging */
        getLastSyncedData: () => lastSyncedData.current
    }
}
